import { memo, useCallback } from 'react';
import { css } from '@linaria/core';
import { classnames } from '@app/common/utils/css';
import { Formatter } from '@app/business/services/formatter';
import { useSdkDemoContext } from './Context';

export interface CartProps {
  onSubmit(): void;
}

export const Cart = memo((props: CartProps) => {
  const { products, quantities, setQuantities, totalAmount } = useSdkDemoContext();

  const updateQuantity = useCallback(
    (quantity: number, index: number) => {
      const newQuantities = [...quantities];
      newQuantities[index] = quantity;

      setQuantities(newQuantities);
    },
    [quantities, setQuantities],
  );

  return (
    <div className={classnames('cart', styles.main)}>
      {products.map((product, index) => (
        <CartItem
          key={index}
          imageUrl={product.imageUrl}
          productName={product.productName}
          price={product.price}
          quantity={quantities[index]}
          onChangeQuantity={(quantity) => updateQuantity(quantity, index)}
        />
      ))}

      <div className="cart_total">
        <label>Total:</label> <span>{Formatter.formatCurrency(totalAmount)}</span>
      </div>

      <button className="checkout_button" onClick={props.onSubmit}>
        Pay with Kontempo
      </button>
    </div>
  );
});

interface CartItemProps {
  imageUrl: string;
  productName: string;
  price: number;
  quantity: number;
  onChangeQuantity(quantity: number): void;
}

const CartItem = memo((props: CartItemProps) => {
  const { quantity, onChangeQuantity } = props;

  const min = 0;
  const max = 10;

  return (
    <div className="cart_item">
      <div className="product_image">
        <img src={props.imageUrl} />
      </div>

      <div className="product_info">
        <div className="product_name">{props.productName}</div>
        <div className="product_price">{Formatter.formatCurrency(props.price)}</div>
      </div>

      <div className="product_quantity">
        <button className="button_increase" onClick={() => onChangeQuantity(Math.min(quantity + 1, max))}>
          +
        </button>

        <input
          className="input_quantity"
          value={quantity}
          onChange={(event) => props.onChangeQuantity(+event.target.value || 1)}
          onBlur={() => {
            if (!quantity || quantity < min) {
              onChangeQuantity(min);
            } else if (quantity > max) {
              onChangeQuantity(max);
            }
          }}
        />

        <button className="button_decrease" onClick={() => onChangeQuantity(Math.max(quantity - 1, min))}>
          -
        </button>
      </div>
    </div>
  );
});

const styles = {
  main: css`
    display: flex;
    flex-direction: column;

    .checkout_button {
      padding: 0.75rem 1.5rem;
      font-size: 0.9rem;
      font-weight: 600;
      background-color: var(--color-primary);
      color: #fff;
      border-radius: 10px;
      margin-top: 2.5rem;
      width: 100%;
      cursor: pointer;
      transition: opacity 0.25s;

      &:hover {
        opacity: 0.75;
      }
    }

    .cart_total {
      font-size: 1.1rem;
      font-weight: 600;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      span {
        color: var(--color-primary);
      }
    }

    .cart_item {
      display: flex;
      flex-direction: row;
      align-items: center;
      background-color: #f7f7fb;
      border-radius: 10px;
      padding: 0.75rem;
      margin-bottom: 1.5rem;

      .product_image {
        width: 4rem;
        height: 4rem;
        background-color: #e0dfdf;
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          width: 80%;
          height: 80%;
          object-fit: contain;
          object-position: center;
        }
      }

      .product_info {
        flex: 1;
        margin: 0 1rem;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }

      .product_name {
        font-size: 1rem;
        color: #222;
      }

      .product_price {
        font-size: 1rem;
        font-weight: 600;
        color: var(--color-primary);
        margin-top: 0.75rem;
      }

      .product_quantity {
        display: flex;
        flex-direction: row;
        align-items: center;

        background-color: #fff;
        border-radius: 5px;
        border: 1px solid #fff;
        overflow: hidden;

        button {
          width: 2rem;
          height: 2rem;
          font-size: 16px;
          font-weight: 400;
          background-color: transparent;
          cursor: pointer;

          &:hover {
            background-color: #fafafa;
          }
        }

        input {
          margin: 0 1px;
          width: 2.5rem;
          height: 1.5rem;
          text-align: center;
          font-size: 0.9rem;
          font-weight: 400;
          border: 1px solid #eee;
          border-top: none;
          border-bottom: none;
        }
      }
    }
  `,
};
